import { GetThreadIO } from '../types';
import { useManualSmsScheduledV1Queries } from './use-manual-sms-scheduled-v1-queries';
import {
  UseManualSmsScheduledV1QueryEndpointArgs,
  useManualSmsScheduledV1Query,
} from './use-manual-sms-scheduled-v1-query';

/**
 * A hook that returns a query for the `GetThread` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetThreadQuery = <E = unknown, D = GetThreadIO['output']>(
  args: UseManualSmsScheduledV1QueryEndpointArgs<'GetThread', E, D>
) =>
  useManualSmsScheduledV1Query({
    endpointName: 'GetThread',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetThread` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetThreadQueries = <E = unknown, D = GetThreadIO['output']>(
  argsArr: UseManualSmsScheduledV1QueryEndpointArgs<'GetThread', E, D>[]
) =>
  useManualSmsScheduledV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetThread',
      ...args,
    }))
  );
